'use client';

import { useCallback, useMemo } from 'react';
import { AnimatePresence } from 'framer-motion';
import { useWorldCupStore, type WorldCupCharacter } from '@/stores/worldCupStore';
import { getAssetUrl } from '@/utils/assetUrl';
import { track } from '@/utils/analytics';
import IntroModal from './IntroModal';
import WorldCupBattle from './WorldCupBattle';
import CharacterReveal from './CharacterReveal';

export interface GreetingData {
  greetingText?: string | null;
  greetingVoicePath?: string | null;
}

interface MuseWorldCupProps {
  characters: WorldCupCharacter[];
  greetings?: Record<string, GreetingData>;
  onComplete: (characterId: string) => void;
  onSkipReveal: (characterId: string) => void;
  onDismiss: () => void;
}

function voiceUrl(path?: string | null): string | null {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  return getAssetUrl(path);
}

export default function MuseWorldCup({
  characters,
  greetings,
  onComplete,
  onSkipReveal,
  onDismiss,
}: MuseWorldCupProps) {
  const phase = useWorldCupStore((s) => s.phase);
  const startWorldCup = useWorldCupStore((s) => s.startWorldCup);
  const finalWinner = useWorldCupStore((s) => s.finalWinner);

  const handleStart = useCallback(() => {
    track('worldcup_started', { candidate_count: characters.length });
    startWorldCup(characters);
  }, [characters, startWorldCup]);

  const handleRevealComplete = useCallback(() => {
    if (finalWinner) {
      onComplete(finalWinner.id);
    }
  }, [finalWinner, onComplete]);

  const handleRevealSkip = useCallback(() => {
    if (finalWinner) {
      onSkipReveal(finalWinner.id);
    }
  }, [finalWinner, onSkipReveal]);

  const resolvedGreetings = useMemo(() => {
    if (!greetings) return undefined;
    const resolved: Record<string, { greetingText?: string | null; greetingVoiceUrl?: string | null }> = {};
    for (const [id, g] of Object.entries(greetings)) {
      resolved[id] = {
        greetingText: g.greetingText,
        greetingVoiceUrl: voiceUrl(g.greetingVoicePath),
      };
    }
    return resolved;
  }, [greetings]);

  const winnerGreeting = finalWinner && resolvedGreetings?.[finalWinner.id];

  return (
    <AnimatePresence mode="wait">
      {phase === 'intro' && (
        <IntroModal
          key="intro"
          characters={characters}
          onStart={handleStart}
          onSkip={onDismiss}
        />
      )}
      {phase === 'playing' && (
        <WorldCupBattle key="playing" greetings={resolvedGreetings} />
      )}
      {phase === 'reveal' && finalWinner && (
        <CharacterReveal
          key="reveal"
          character={finalWinner}
          greetingText={winnerGreeting?.greetingText}
          greetingVoiceUrl={winnerGreeting?.greetingVoiceUrl}
          onComplete={handleRevealComplete}
          onSkip={handleRevealSkip}
        />
      )}
    </AnimatePresence>
  );
}
